import { useCallback, useRef, useEffect } from 'react'
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  MDXEditor,
  type MDXEditorMethods,
  BlockTypeSelect,
  quotePlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  headingsPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  InsertCodeBlock,
  CodeToggle,
  ListsToggle,
  listsPlugin,
  toolbarPlugin,
  InsertTable,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import '@/styles/mdxeditor.css'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

export interface FormMdxEditorProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  description?: string
  placeholder?: string
  minHeight?: string
  maxHeight?: string
  disabled?: boolean
  /** Show diff view toggle (for revision comparison) */
  showDiffView?: boolean
  /** Original markdown for diff comparison */
  diffMarkdown?: string
}

/**
 * Preprocesses markdown to escape problematic sequences
 * that could cause parsing issues
 */
function preprocessMarkdown(markdown: string): string {
  // Escape <= sequences that aren't already escaped
  // This prevents Jinja-like template syntax from breaking the parser
  return markdown?.replace(/(?<!\\)<=/g, '\\<=') || ''
}

export function FormMdxEditor<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  minHeight = '200px',
  maxHeight = '600px',
  disabled = false,
  showDiffView = true,
  diffMarkdown,
}: FormMdxEditorProps<TFieldValues>) {
  const { t } = useTranslation()
  const { resolvedTheme } = useUIStore()
  const isDarkMode = resolvedTheme === 'dark'

  const editorRef = useRef<MDXEditorMethods>(null)
  const originalValueRef = useRef<string>('')

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  // Store original value for diff comparison
  useEffect(() => {
    if (diffMarkdown !== undefined) {
      originalValueRef.current = diffMarkdown
    } else if (value && !originalValueRef.current) {
      originalValueRef.current = value
    }
  }, [diffMarkdown, value])

  // Update editor content when value changes externally
  useEffect(() => {
    if (value !== undefined && editorRef.current) {
      const processedValue = preprocessMarkdown(value)
      // Only update if the markdown is different (avoid cursor jump)
      const currentMarkdown = editorRef.current.getMarkdown()
      if (currentMarkdown !== processedValue) {
        editorRef.current.setMarkdown(processedValue)
      }
    }
  }, [value])

  // Memoize the onChange handler to avoid re-renders
  const handleEditorChange = useCallback(
    (newMarkdown: string) => {
      // Use setTimeout to avoid React state update during render
      setTimeout(() => {
        onChange(newMarkdown)
      }, 0)
    },
    [onChange]
  )

  // Handle parsing errors gracefully
  const handleError = useCallback((payload: { error: string; source: string }) => {
    console.error('MDXEditor Parsing Error:', payload)
  }, [])

  const hasError = !!error

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
          {label}
        </label>
      )}
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      )}
      <div
        className={cn(
          'bonnie-mdx-editor notranslate',
          hasError && 'has-error',
          disabled && 'pointer-events-none opacity-50'
        )}
        style={{
          '--editor-min-height': minHeight,
          '--editor-max-height': maxHeight,
        } as React.CSSProperties}
      >
        <MDXEditor
          ref={editorRef}
          className={isDarkMode ? 'dark-editor' : ''}
          markdown={preprocessMarkdown(value ?? '')}
          placeholder={placeholder}
          onError={handleError}
          onChange={handleEditorChange}
          readOnly={disabled}
          contentEditableClassName="mdxeditor-root-contenteditable"
          plugins={[
            // Basic formatting
            quotePlugin(),
            listsPlugin(),
            headingsPlugin(),
            tablePlugin(),

            // Code blocks with Jinja syntax highlighting
            codeBlockPlugin({ defaultCodeBlockLanguage: 'jinja' }),
            codeMirrorPlugin({
              codeBlockLanguages: {
                jinja: 'Template',
                javascript: 'JavaScript',
                typescript: 'TypeScript',
                python: 'Python',
                json: 'JSON',
                html: 'HTML',
                css: 'CSS',
                markdown: 'Markdown',
              },
            }),

            // Diff source for comparing changes
            diffSourcePlugin({
              diffMarkdown: preprocessMarkdown(
                diffMarkdown ?? originalValueRef.current
              ),
              viewMode: 'rich-text',
            }),

            // Toolbar
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  {showDiffView ? (
                    <DiffSourceToggleWrapper>
                      <UndoRedo />
                      <BlockTypeSelect />
                      <BoldItalicUnderlineToggles />
                      <CodeToggle />
                      <InsertCodeBlock />
                      <ListsToggle options={['bullet']} />
                      <InsertTable />
                    </DiffSourceToggleWrapper>
                  ) : (
                    <>
                      <UndoRedo />
                      <BlockTypeSelect />
                      <BoldItalicUnderlineToggles />
                      <CodeToggle />
                      <InsertCodeBlock />
                      <ListsToggle options={['bullet']} />
                      <InsertTable />
                    </>
                  )}
                </>
              ),
            }),
          ]}
        />
      </div>
      {hasError && (
        <p className="text-sm text-error-600 dark:text-error-400">{t(error.message ?? '')}</p>
      )}
    </div>
  )
}

export default FormMdxEditor
